import React, { useEffect, useState } from "react";
import { Box, Paper, TextField, Typography, Button, useTheme, Divider } from "@mui/material";
import Header from "../../components/Header";
import {
  useGetHomeQuery,
  useUpdateHomeMutation,
  useGetAboutQuery,
  useUpdateAboutMutation,
} from "../../services/siteContent.service";
import StringListEditor from "./StringListEditor";
import GroupedListEditor from "./GroupedListEditor";

const EditSiteContent = () => {
  const theme = useTheme();

  const { data: homeData } = useGetHomeQuery();
  const [updateHome, { isLoading: savingHome }] = useUpdateHomeMutation();
  const [homeLoaded, setHomeLoaded] = useState(false);
  const [homeTitle, setHomeTitle] = useState("");
  const [homeSubtitle, setHomeSubtitle] = useState("");
  const [paragraphs, setParagraphs] = useState([]);
  const [homeMessage, setHomeMessage] = useState("");

  useEffect(() => {
    if (homeData && !homeLoaded) {
      setHomeTitle(homeData.title || "");
      setHomeSubtitle(homeData.subtitle || "");
      setParagraphs(homeData.content ? homeData.content.split(";").map((p) => p.trim()) : []);
      setHomeLoaded(true);
    }
  }, [homeData, homeLoaded]);

  const handleSaveHome = () => {
    setHomeMessage("");
    updateHome({ title: homeTitle, subtitle: homeSubtitle, paragraphs })
      .unwrap()
      .then(() => setHomeMessage("Home page saved."))
      .catch(() => setHomeMessage("Failed to save home page."));
  };

  const { data: aboutData } = useGetAboutQuery();
  const [updateAbout, { isLoading: savingAbout }] = useUpdateAboutMutation();
  const [aboutLoaded, setAboutLoaded] = useState(false);
  const [aboutTitle, setAboutTitle] = useState("");
  const [aboutSubtitle, setAboutSubtitle] = useState("");
  const [educItems, setEducItems] = useState([]);
  const [researchItems, setResearchItems] = useState([]);
  const [teachItems, setTeachItems] = useState([]);
  const [langItems, setLangItems] = useState([]);
  const [certGroups, setCertGroups] = useState([]);
  const [membershipGroups, setMembershipGroups] = useState([]);
  const [aboutMessage, setAboutMessage] = useState("");

  useEffect(() => {
    if (aboutData && !aboutLoaded) {
      setAboutTitle(aboutData.title || "");
      setAboutSubtitle(aboutData.subtitle || "");
      setEducItems(aboutData.educItems || []);
      setResearchItems(aboutData.researchItems || []);
      setTeachItems(aboutData.teachItems || []);
      setLangItems(aboutData.langItems || []);
      setCertGroups(
        Object.entries(aboutData.certItems || {}).map(([name, items]) => ({ name, items }))
      );
      setMembershipGroups(
        Object.entries(aboutData.membershipItems || {}).map(([name, items]) => ({ name, items }))
      );
      setAboutLoaded(true);
    }
  }, [aboutData, aboutLoaded]);

  const handleSaveAbout = () => {
    setAboutMessage("");
    updateAbout({
      title: aboutTitle,
      subtitle: aboutSubtitle,
      educItems,
      researchItems,
      teachItems,
      langItems,
      certGroups,
      membershipGroups,
    })
      .unwrap()
      .then(() => setAboutMessage("About page saved."))
      .catch(() => setAboutMessage("Failed to save about page."));
  };

  return (
    <Box m="1.5rem 2.5rem">
      <Header title="Edit Site Content" subtitle="Manage the Home and About page content" />

      <Paper sx={{ p: "1.5rem", mt: "1.5rem", backgroundColor: theme.palette.background.alt }}>
        <Typography variant="h5" fontWeight={700} sx={{ mb: "1rem" }}>
          Home Page
        </Typography>
        <TextField
          fullWidth
          label="Title"
          value={homeTitle}
          onChange={(e) => setHomeTitle(e.target.value)}
          sx={{ mb: "1rem" }}
        />
        <TextField
          fullWidth
          label="Subtitle"
          value={homeSubtitle}
          onChange={(e) => setHomeSubtitle(e.target.value)}
          sx={{ mb: "1rem" }}
        />
        <StringListEditor label="Paragraphs" items={paragraphs} onChange={setParagraphs} />
        <Button variant="contained" onClick={handleSaveHome} disabled={savingHome}>
          Save Home Page
        </Button>
        {homeMessage && (
          <Typography sx={{ mt: "0.5rem" }} color={homeMessage.startsWith("Failed") ? "error" : "success.main"}>
            {homeMessage}
          </Typography>
        )}
      </Paper>

      <Paper sx={{ p: "1.5rem", mt: "1.5rem", backgroundColor: theme.palette.background.alt }}>
        <Typography variant="h5" fontWeight={700} sx={{ mb: "1rem" }}>
          About Page
        </Typography>
        <TextField
          fullWidth
          label="Title"
          value={aboutTitle}
          onChange={(e) => setAboutTitle(e.target.value)}
          sx={{ mb: "1rem" }}
        />
        <TextField
          fullWidth
          label="Subtitle"
          value={aboutSubtitle}
          onChange={(e) => setAboutSubtitle(e.target.value)}
          sx={{ mb: "1rem" }}
        />
        <StringListEditor label="Education" items={educItems} onChange={setEducItems} />
        <StringListEditor
          label="Research and Employment"
          items={researchItems}
          onChange={setResearchItems}
        />
        <StringListEditor label="Teaching" items={teachItems} onChange={setTeachItems} />
        <StringListEditor label="Languages" items={langItems} onChange={setLangItems} />
        <Divider sx={{ my: "1rem" }} />
        <GroupedListEditor title="Certifications" groups={certGroups} onChange={setCertGroups} />
        <Divider sx={{ my: "1rem" }} />
        <GroupedListEditor
          title="Memberships"
          groups={membershipGroups}
          onChange={setMembershipGroups}
        />
        <Button variant="contained" onClick={handleSaveAbout} disabled={savingAbout}>
          Save About Page
        </Button>
        {aboutMessage && (
          <Typography sx={{ mt: "0.5rem" }} color={aboutMessage.startsWith("Failed") ? "error" : "success.main"}>
            {aboutMessage}
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default EditSiteContent;
